/**
 * browser 全体のエラートースト文言を例外種別ごとに生成する。
 */
import type { toast } from '@/hooks/useToast'
import type { FailedEvmAddressChainDetail } from '@pkg/basic'
import {
  AuthTokenExpiredException,
  FailedToAnalyzeEvmChainException,
  FailedToAnalyzeEvmTxException,
  FailedToAnalyzeExchangeApiException,
  FailedToAnalyzeExchangeException,
  FailedToAnalyzePolkadotChainException,
  FailedToAnalyzeSolanaException,
  FailedToAnalyzeSolanaTxException,
  FailedToFetchEvmIndexDataException,
  FailedToFetchEvmTxsException,
  FailedToFetchPolkadotBlockDataException,
  FailedToFetchPolkadotIndexDataException,
  FailedToFetchSolanaAddressException,
  FailedToFetchSolanaTxException,
  FailedToRegisterEvmAddressException,
  RateLimitExceededException,
  UnwritableDatabaseException,
} from '@pkg/basic'
import {
  ApiAuthenticationFailedException,
  ExchangeApiConfigurationException,
} from '@pkg/exchange-api'
import { captureException } from '@sentry/react'

/**
 * EVM address 登録失敗の詳細をトースト向け文字列に整形する
 * @param params - 整形パラメータ
 * @param params.details - address ごとの失敗チェーン一覧
 * @returns トースト表示用の説明文
 */
export const formatFailedToRegisterEvmAddressDescription = (params: {
  details: FailedEvmAddressChainDetail[]
}): string => {
  const { details } = params
  const formattedDetails = details
    .map(
      ({ address, failedChains }) => `${address} (${failedChains.join(', ')})`,
    )
    .join(' / ')

  return `一部チェーンの解析に失敗しました: ${formattedDetails}`
}

/**
 * 取引所 API の同期失敗トーストの説明文を生成する。
 * 設定不備が原因の場合は、時間を置いても解消しないため具体的な対処法を示す。
 * @param params - 取引所名、API 表示名、同期失敗の原因例外
 * @returns トーストへ表示する説明文
 */
export const formatExchangeApiErrorDescription = ({
  exchangeName,
  api,
  cause,
}: {
  readonly exchangeName: string
  readonly api: string
  readonly cause: unknown
}): string => {
  const target = `「${exchangeName}: ${api}」`

  if (cause instanceof ExchangeApiConfigurationException) {
    switch (cause.reason) {
      case 'bitget-unified-account': {
        return `${target}の同期に失敗しました。Bitget アカウントが Unified Account (UTA) のため、この API はまだ取得に対応していません。入出金履歴と取引履歴は UTA でも同期されます。`
      }
      case 'bitget-classic-account': {
        return `${target}の同期に失敗しました。Bitget アカウントが Classic Account のため、Unified Account (UTA) 向けの取得を利用できません。`
      }
      case 'ip-not-allowed': {
        return `${target}の同期に失敗しました。API Key に IP 制限が設定されています。取引所側で IP 制限を解除するか、IP 制限なしの API Key を再発行してください。`
      }
      case 'signature-verification-failed': {
        return `${target}の同期に失敗しました。API Key・Secret・Passphrase をご確認ください。Bitget の場合、Unified Account (UTA) へ移行済みだと現在は同期できません。`
      }
      default: {
        return `${target}の同期に失敗しました。取引所側の API 設定をご確認ください。`
      }
    }
  }

  return `${target}のデータ同期に失敗しました。しばらく時間を置いてから再度お試しください。`
}

/**
 * アプリ全体で利用するエラートーストを例外種別ごとに表示する。
 * @param params - 表示対象の例外と toast 関数
 */
export const showErrorToast = (params: {
  error: unknown
  toast: typeof toast
}) => {
  const { error, toast } = params
  let stopPropagation = false
  if (
    error instanceof Error &&
    error.message.includes('Failed to fetch dynamically imported module')
  ) {
    stopPropagation = true
    window.location.reload()
  } else if (error instanceof AuthTokenExpiredException) {
    toast({
      variant: 'destructive',
      description: '認証に失敗しました',
    })
    stopPropagation = true
  } else if (error instanceof UnwritableDatabaseException) {
    toast({
      variant: 'destructive',
      description: '読み取り専用モードではデータ更新できません',
    })
    stopPropagation = true
  } else if (error instanceof RateLimitExceededException) {
    toast({
      variant: 'destructive',
      description: `${error.accountName} のデータ取得量が上限に達しました。明日以降に再度お試しください。`,
    })
    stopPropagation = true
  } else if (error instanceof ApiAuthenticationFailedException) {
    toast({
      variant: 'destructive',
      description: `「${error.category}」の API 認証に失敗しました。登録している API Key 等が正しいか確認してください。`,
    })
    stopPropagation = true
  } else if (error instanceof FailedToRegisterEvmAddressException) {
    toast({
      variant: 'destructive',
      description: formatFailedToRegisterEvmAddressDescription({
        details: error.details,
      }),
    })
  } else if (error instanceof FailedToAnalyzeExchangeException) {
    toast({
      variant: 'destructive',
      description: `${error.exchangeName} のデータ同期に失敗しました。しばらく時間を置いてから再度お試しください。`,
    })
  } else if (error instanceof FailedToAnalyzeExchangeApiException) {
    const api = parseToExchangeApiName({
      exchange: error.exchangeName,
      apiId: error.apiId,
    })
    toast({
      variant: 'destructive',
      description: formatExchangeApiErrorDescription({
        exchangeName: error.exchangeName,
        api,
        cause: error.cause,
      }),
    })
  } else if (error instanceof FailedToAnalyzeEvmChainException) {
    toast({
      variant: 'destructive',
      description: `${error.chainName} のデータ同期に失敗しました。しばらく時間を置いてから再度お試しください。`,
    })
  } else if (error instanceof FailedToFetchEvmIndexDataException) {
    toast({
      variant: 'destructive',
      description: `${error.address} (${error.chainName}) のデータ取得に失敗しました。しばらく時間を置いてから再度お試しください。`,
    })
  } else if (error instanceof FailedToFetchEvmTxsException) {
    toast({
      variant: 'destructive',
      description: `${error.chainName} の ${error.hashes.length} 件の取引データ取得に失敗しました。しばらく時間を置いてから再度お試しください。`,
    })
  } else if (error instanceof FailedToAnalyzeEvmTxException) {
    toast({
      variant: 'destructive',
      description: `${error.hash} (${error.chainName}) のデータ解析に失敗しました。しばらく時間を置いてから再度お試しください。`,
    })
  } else if (error instanceof FailedToAnalyzePolkadotChainException) {
    toast({
      variant: 'destructive',
      description: `${error.chainName} のデータ同期に失敗しました。しばらく時間を置いてから再度お試しください。`,
    })
  } else if (error instanceof FailedToFetchPolkadotIndexDataException) {
    toast({
      variant: 'destructive',
      description: `${error.address} (${error.chainName}) のデータ取得に失敗しました。しばらく時間を置いてから再度お試しください。`,
    })
  } else if (error instanceof FailedToFetchPolkadotBlockDataException) {
    toast({
      variant: 'destructive',
      description: `${error.chainName} のブロック (${error.blockNumber}) データ取得に失敗しました。しばらく時間を置いてから再度お試しください。`,
    })
  } else if (error instanceof FailedToAnalyzeSolanaException) {
    toast({
      variant: 'destructive',
      description:
        'Solana のデータ同期に失敗しました。しばらく時間を置いてから再度お試しください。',
    })
  } else if (error instanceof FailedToFetchSolanaTxException) {
    toast({
      variant: 'destructive',
      description: `Solana tx \
${error.signature} のデータ取得に失敗しました。しばらく時間を置いてから再度お試しください。`,
    })
  } else if (error instanceof FailedToFetchSolanaAddressException) {
    toast({
      variant: 'destructive',
      description: `Solana address \
${error.address} のデータ取得に失敗しました。しばらく時間を置いてから再度お試しください。`,
    })
  } else if (error instanceof FailedToAnalyzeSolanaTxException) {
    toast({
      variant: 'destructive',
      description: `Solana tx \
${error.signature} のデータ解析に失敗しました。しばらく時間を置いてから再度お試しください。`,
    })
  } else if (error instanceof Error) {
    toast({
      variant: 'destructive',
      description: error.message || 'エラーが発生しました',
    })
  }
  if (!stopPropagation) {
    captureException(error)
    console.error(error)
  }
}

/**
 * 取引所 API ID をユーザー向けの日本語名称へ変換する。
 * @param params - 取引所名と API ID
 * @returns 表示用の API 名称
 */
const parseToExchangeApiName = (params: {
  exchange: string
  apiId: string
}): string => {
  const { exchange, apiId } = params
  switch (exchange) {
    case 'bitbank': {
      switch (apiId) {
        case 'deposit': {
          return '入金履歴'
        }
        case 'trade': {
          return '取引履歴'
        }
        case 'withdrawal': {
          return '出金履歴'
        }
        default: {
          return `exchange-api/${exchange}/${apiId}`
        }
      }
    }
    case 'bitget': {
      switch (apiId) {
        case 'deposit': {
          return '入金履歴'
        }
        case 'future-tax': {
          return '先物履歴'
        }
        case 'spot-tax': {
          return '現物履歴'
        }
        case 'withdrawal': {
          return '出金履歴'
        }
        default: {
          return `exchange-api/${exchange}/${apiId}`
        }
      }
    }
    case 'bybit': {
      switch (apiId) {
        case 'deposit': {
          return '入金履歴'
        }
        case 'linear-closed-pl': {
          return 'インバース実現損益履歴'
        }
        case 'spot-trade': {
          return '現物取引履歴'
        }
        case 'withdrawal': {
          return '出金履歴'
        }
        default: {
          return `exchange-api/${exchange}/${apiId}`
        }
      }
    }
    case 'gate': {
      switch (apiId) {
        case 'deposit': {
          return '入金履歴'
        }
        case 'withdrawal': {
          return '出金履歴'
        }
        case 'spot-trade': {
          return '現物取引履歴'
        }
        default: {
          return `exchange-api/${exchange}/${apiId}`
        }
      }
    }
    case 'kucoin': {
      switch (apiId) {
        case 'deposit': {
          return '入金履歴'
        }
        case 'withdrawal': {
          return '出金履歴'
        }
        case 'spot-trade': {
          return '現物取引履歴'
        }
        default: {
          return `exchange-api/${exchange}/${apiId}`
        }
      }
    }
    default: {
      return `exchange-api/${exchange}/${apiId}`
    }
  }
}
